<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  actions: { type: Object, default: null },
});

const showInfo = ref(false);
const labels = computed(() =>
  [
    ...(props.actions?.macros || []).map(item => ({
      ...item,
      conditional: false,
      source: 'macro',
    })),
    ...(props.actions?.automations || []).map(item => ({
      ...item,
      conditional: true,
      source: 'automation',
    })),
  ]
    .filter(item => item.available && (item.source === 'macro' || item.active))
    .flatMap(item =>
      (item.labels || []).flatMap(change =>
        (change.names || []).map(name => ({
          name,
          operation: change.operation,
          conditional: item.conditional,
          source: item.source,
          sourceName: item.name,
        }))
      )
    )
);
const hasActions = computed(
  () =>
    labels.value.length ||
    props.actions?.macros?.length ||
    props.actions?.automations?.length
);
</script>

<template>
  <div class="min-w-0">
    <section
      class="min-w-0 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 text-sm"
      :aria-label="$t('CRM.nextStagePreview.title')"
    >
      <div class="flex items-center gap-2 bg-n-solid-3 px-3 py-2.5">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ $t('CRM.nextStagePreview.title') }}
        </h3>
        <button
          type="button"
          class="flex items-center text-n-slate-11 hover:text-n-slate-12"
          :aria-label="$t('CRM.nextStagePreview.info')"
          :aria-expanded="showInfo"
          @click="showInfo = !showInfo"
        >
          <span class="i-lucide-info size-4" aria-hidden="true" />
        </button>
      </div>
      <p
        v-if="showInfo"
        class="m-0 border-b border-n-weak bg-n-solid-3 px-3 pb-2 text-xs text-n-slate-11"
      >
        {{ $t('CRM.nextStagePreview.disclaimer') }}
        {{ $t('CRM.nextStagePreview.indirect') }}
      </p>
      <div v-if="hasActions" class="flex flex-col gap-3 p-3">
        <div
          v-if="labels.length"
          class="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2"
        >
          <div class="flex items-center gap-2 pt-1 text-n-slate-12">
            <span class="i-lucide-tag size-4 shrink-0" aria-hidden="true" />
            <span>{{ $t('CRM.nextStagePreview.labels') }}</span>
          </div>
          <ul class="m-0 flex min-w-0 list-none flex-wrap gap-1.5 p-0">
            <li
              v-for="(change, index) in labels"
              :key="`${change.operation}-${change.name}-${index}`"
              :title="
                $t(`CRM.nextStagePreview.${change.source}`, {
                  name: change.sourceName,
                })
              "
              class="inline-flex max-w-full items-center gap-1 rounded-lg px-2 py-1 text-xs"
              :class="
                change.operation === 'remove_label'
                  ? 'bg-n-ruby-3 text-n-ruby-11'
                  : 'bg-n-blue-3 text-n-blue-11'
              "
            >
              <span
                :class="
                  change.operation === 'remove_label'
                    ? 'i-lucide-minus'
                    : 'i-lucide-plus'
                "
                class="size-3 shrink-0"
                aria-hidden="true"
              />
              <span class="break-words">{{ change.name }}</span>
              <span v-if="change.conditional" class="sr-only">
                {{ $t('CRM.nextStagePreview.conditional') }}
              </span>
            </li>
          </ul>
        </div>
        <div
          v-if="actions?.macros?.length"
          class="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2"
        >
          <div class="flex items-center gap-2 pt-1 text-n-slate-12">
            <span
              class="i-lucide-message-square size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{{ $t('CRM.nextStagePreview.macros') }}</span>
          </div>
          <ul class="m-0 flex min-w-0 list-none flex-wrap gap-1.5 p-0">
            <li
              v-for="item in actions.macros"
              :key="item.id"
              class="max-w-full break-words rounded-lg bg-n-solid-3 px-2 py-1 text-xs text-n-slate-12"
            >
              {{ item.name || `#${item.id}` }}
              <span v-if="!item.available" class="text-n-slate-11">
                {{ $t('CRM.nextStagePreview.unavailable') }}
              </span>
            </li>
          </ul>
        </div>
        <div
          v-if="actions?.automations?.length"
          class="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2"
        >
          <div class="flex items-center gap-2 pt-1 text-n-slate-12">
            <span class="i-lucide-zap size-4 shrink-0" aria-hidden="true" />
            <span>{{ $t('CRM.nextStagePreview.automations') }}</span>
          </div>
          <ul class="m-0 flex min-w-0 list-none flex-wrap gap-1.5 p-0">
            <li
              v-for="item in actions.automations"
              :key="item.id"
              class="max-w-full break-words rounded-lg bg-n-solid-3 px-2 py-1 text-xs text-n-slate-12"
            >
              {{ item.name || `#${item.id}` }}
              <span class="text-n-slate-11">
                {{
                  $t(
                    `CRM.nextStagePreview.${!item.available ? 'unavailable' : item.active ? 'conditionalShort' : 'inactive'}`
                  )
                }}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <p v-else class="m-0 p-3 text-xs text-n-slate-11">
        {{ $t('CRM.nextStagePreview.none') }}
      </p>
    </section>
    <p
      v-if="labels.some(change => change.conditional)"
      class="mb-0 mt-2 text-xs text-n-slate-11"
    >
      {{ $t('CRM.nextStagePreview.conditionalHelp') }}
    </p>
  </div>
</template>
